import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
} from "react";
import { useTranslation } from "react-i18next";
import BrandIcon from "../BrandIcon";
import AttachButton from "../AttachButton";
import AttachmentPreviews from "../AttachmentPreviews";
import { tokens } from "../../theme";
import type { WidgetFeatures } from "../../types/api";
import { DEFAULT_FEATURES } from "../../lib/configHelpers";
import { MAX_ATTACHMENTS, canSendWith } from "../../lib/attachments";
import type { AttachmentNotice } from "../../hooks/useAttachments";
import type { ComposerAttachment } from "../../types/chat";

// Field grows 1 → MAX_LINES lines, then scrolls inside.
const LINE_HEIGHT = 22;
const FIELD_PADDING_Y = 7;
const MAX_LINES = 5;
const SINGLE_LINE_HEIGHT = LINE_HEIGHT + FIELD_PADDING_Y * 2;
const MAX_FIELD_HEIGHT = LINE_HEIGHT * MAX_LINES + FIELD_PADDING_Y * 2;

interface MobileComposerProps {
  name: string;
  onSend: (text: string) => void;
  disabled?: boolean;
  features?: WidgetFeatures;
  /** Files picked via the attach button (shown only when features.file_upload) */
  onAttach?: (files: File[]) => void;
  attachments?: ComposerAttachment[];
  onRemoveAttachment?: (id: string) => void;
  onRetryAttachment?: (id: string) => void;
  attachmentNotice?: AttachmentNotice | null;
}

const styles = {
  footer: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    padding: `${tokens.space[2]} ${tokens.space[4]} 0`,
    paddingBottom: `max(var(--avatar-ime-bottom, 0px), var(--avatar-kb-safe-bottom))`,
    background: tokens.surface.canvas,
  },
  fieldShell: {
    padding: 4,
    border: `1.5px solid ${tokens.surface.elevated}`,
    borderRadius: tokens.radius.pill,
    background: tokens.surface.elevated,
    boxSizing: 'border-box',
    transition: 'border-radius 150ms ease',
  },
  // Bordered box — a pill normally, a rounded card once it holds previews or
  // wraps to more than one line.
  fieldBox: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.pill,
    background: tokens.surface.input,
    transition: "border-radius 150ms ease",
  },
  // 24px = half the 48px row, so the bottom row's ends stay round.
  fieldBoxExpanded: {
    borderRadius: 24,
  },
  fieldShellExpanded: {
    borderRadius: 28,
  },
  previews: {
    paddingTop: 10,
    paddingLeft: 10,
    paddingRight: 10,
  },
  // flex-end: buttons stay on the last line while the field grows upward.
  fieldWrap: {
    display: "flex",
    alignItems: "flex-end",
    gap: tokens.space[2],
    minHeight: 46,
    boxSizing: "border-box",
    padding: `5px 6px 5px ${tokens.space[4]}`,
  },
  // 30px circle next to 36px buttons — lift it so their centres line up.
  attachBtn: {
    marginBottom: 3,
  },
  // Centres the 30px attach circle in the 48px pill.
  fieldWrapWithAttach: {
    paddingLeft: 8,
  },
  field: {
    flex: 1,
    minWidth: 0,
    height: SINGLE_LINE_HEIGHT,
    maxHeight: MAX_FIELD_HEIGHT,
    margin: 0,
    padding: `${FIELD_PADDING_Y}px 0`,
    boxSizing: "border-box",
    resize: "none",
    overflowY: "hidden",
    border: "none",
    background: "transparent",
    outline: "none",
    boxShadow: "none",
    appearance: "none",
    WebkitAppearance: "none",
    // 16px stops iOS from zooming in on focus.
    fontSize: "16px",
    lineHeight: `${LINE_HEIGHT}px`,
    fontFamily: "inherit",
    color: tokens.text.primary,
  },
  micBtn: {
    width: 36,
    height: 36,
    border: "none",
    background: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: tokens.gradient.accent,
    color: tokens.text.onBrand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: tokens.shadow.send,
    padding: 0,
    flexShrink: 0,
  },
  powered: {
    margin: `${tokens.space[2]} 0 0`,
    paddingBottom: tokens.space[2],
    textAlign: "center",
    fontSize: 14,
    color: tokens.text.muted,
    fontWeight: tokens.font.weight.medium,
  },
} as const satisfies Record<string, CSSProperties>;

export default function MobileComposer({
  name,
  onSend,
  disabled,
  features = DEFAULT_FEATURES,
  onAttach,
  attachments = [],
  onRemoveAttachment,
  onRetryAttachment,
  attachmentNotice,
}: MobileComposerProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [multiline, setMultiline] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const canSend = canSendWith(value, attachments) && !disabled;
  const idle = !canSend;
  const hasPreviews = attachments.length > 0 || Boolean(attachmentNotice);
  const expanded = hasPreviews || multiline;

  const fitToContent = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    const content = el.scrollHeight;
    el.style.height = `${Math.min(content, MAX_FIELD_HEIGHT)}px`;
    el.style.overflowY = content > MAX_FIELD_HEIGHT ? "auto" : "hidden";
    setMultiline(content > SINGLE_LINE_HEIGHT + 1);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    fitToContent(event.target);
  };

  useEffect(() => {
    if (disabled) inputRef.current?.blur();
  }, [disabled]);

  const submit = () => {
    if (!canSend) return;
    onSend(value.trim());
    setValue("");
    const el = inputRef.current;
    if (el) {
      el.style.height = "";
      el.style.overflowY = "";
    }
    setMultiline(false);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  return (
    <footer style={styles.footer}>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            ...styles.fieldShell,
            ...(expanded ? styles.fieldShellExpanded : null),
          }}
        >
          <div
            style={{
              ...styles.fieldBox,
              ...(expanded ? styles.fieldBoxExpanded : null),
            }}
          >
          {hasPreviews && onRemoveAttachment ? (
            <AttachmentPreviews
              attachments={attachments}
              onRemove={onRemoveAttachment}
              onRetry={onRetryAttachment}
              notice={attachmentNotice}
              style={styles.previews}
            />
          ) : null}
          <div
            style={{
              ...styles.fieldWrap,
              ...(features.file_upload ? styles.fieldWrapWithAttach : null),
            }}
          >
          {features.file_upload ? (
            <AttachButton
              onAttach={onAttach}
              disabled={disabled}
              full={attachments.length >= MAX_ATTACHMENTS}
                remaining={MAX_ATTACHMENTS - attachments.length}
              style={styles.attachBtn}
            />
          ) : null}
          {/* Enter inserts a new line on mobile; sending is the ➤ button only. */}
          <textarea
            ref={inputRef}
            rows={1}
            style={{
              ...styles.field,
              pointerEvents: disabled ? 'none' : 'auto',
            }}
            value={value}
            onChange={handleChange}
            onFocus={(e) => {
              if (disabled) e.currentTarget.blur();
            }}
            placeholder={t("mobile.placeholder", { name })}
            readOnly={disabled}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            enterKeyHint="enter"
            autoComplete="off"
            autoCorrect="off"
            aria-label={t("input.ariaLabel")}
          />
          {features.voice ? (
            <button
              type="button"
              style={styles.micBtn}
              disabled={disabled}
              aria-label={t("mobile.mic")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="9"
                  y="3"
                  width="6"
                  height="11"
                  rx="3"
                  stroke={tokens.text.secondary}
                  strokeWidth="1.8"
                />
                <path
                  d="M6 11a6 6 0 0012 0"
                  stroke={tokens.text.secondary}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M12 17v3"
                  stroke={tokens.text.secondary}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
          <button
            type="submit"
            style={{
              ...styles.sendBtn,
              opacity: idle ? 0.45 : 1,
              boxShadow: idle ? "none" : tokens.shadow.send,
              cursor: canSend ? "pointer" : "default",
            }}
            disabled={!canSend}
            aria-label={t("input.send")}
          >
            <BrandIcon
              name="sendWhite"
              size={16}
              style={{ position: "relative", top: -1, left: 1 }}
            />
          </button>
          </div>
          </div>
        </div>
      </form>
      {/* Always shown in Beta (BR-12); branding.show_powered_by applies from GA tiers. */}
      <p style={styles.powered} data-powered-by="">
        {t("input.poweredBy")}
      </p>
    </footer>
  );
}
